import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { mensajeDeCampo, tieneCodigo } from '../api';
import type { TenantResumen } from '../api';
import { nombreDeTenant } from '../tenants';
import { altaDeSistema, crearSistema } from './api';
import { useRefrescarSistemas } from './consultas';
import { KeyEnClaro } from './KeyEnClaro';
import { mensajeDeSistemas } from './mensajes';

/**
 * Alta de un sistema conectado (KAN-144).
 *
 * # LOS TENANTS SE ELIGEN ACÁ, POR NOMBRE
 *
 * Se pueden marcar en el alta o dejarse para después: un sistema sin tenants es
 * válido —se autentica y no puede emitir para nadie—, que es además el estado más
 * seguro para empezar. Lo que no hay en ninguna parte de esta pantalla es dónde
 * escribir un id: se marcan por razón social y CUIT.
 *
 * # LO QUE PASA AL TERMINAR
 *
 * El `201` trae la **API key en claro**, y es la única vez que va a existir fuera
 * de quien la pidió. Por eso no hay un discreto "listo, creado" al pie: se abre
 * el panel de {@link KeyEnClaro}, que tapa la pantalla y no se cierra hasta que
 * la persona confirme que la guardó.
 */
export function FormularioDeAltaDeSistema({ tenants }: { tenants: readonly TenantResumen[] }) {
  const idNombre = useId();

  const [nombre, setNombre] = useState('');
  const [elegidos, setElegidos] = useState<readonly number[]>([]);

  const refrescar = useRefrescarSistemas();

  const alta = useMutation({
    mutationFn: () => crearSistema(altaDeSistema({ nombre, tenants: elegidos })),
    // `gcTime: 0` NO es una optimización: ver el comentario gemelo en
    // `FilaDeSistema`. Sin esto la key en claro se queda 5 minutos en el
    // `MutationCache` del cliente después de cerrado el panel.
    gcTime: 0,
    onSuccess: () => {
      setNombre('');
      setElegidos([]);
      refrescar();
      // La key NO se copia a ningún estado: se lee de `alta.data` mientras el
      // panel está abierto y se borra con `alta.reset()` al cerrarlo.
    },
  });

  const errorDeNombre =
    mensajeDeCampo(alta.error, 'nombre') ??
    (tieneCodigo(alta.error, 'ya_existe') ? 'Ya está en uso.' : null);

  function alternar(tenantId: number): void {
    setElegidos((previos) =>
      previos.includes(tenantId)
        ? previos.filter((id) => id !== tenantId)
        : [...previos, tenantId],
    );
  }

  return (
    <form
      onSubmit={(evento) => {
        evento.preventDefault();
        alta.mutate();
      }}
      noValidate
    >
      <h2>Dar de alta un sistema conectado</h2>
      <p className="nota">
        Una cuenta de servicio: una aplicación que emite comprobantes con una API key. No es una
        persona — no entra a esta pantalla ni administra nada, solo emite.
      </p>

      <p>
        <label htmlFor={idNombre}>Nombre</label>
        <br />
        <input
          id={idNombre}
          name="nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          aria-invalid={errorDeNombre !== null}
          aria-describedby={errorDeNombre ? `${idNombre}-error` : undefined}
          required
        />
        {errorDeNombre ? (
          <span id={`${idNombre}-error`} className="error">
            {' '}
            {errorDeNombre}
          </span>
        ) : null}
        <br />
        <span className="nota">
          Con qué nombre se lo va a reconocer en la lista y en los logs. Es único.
        </span>
      </p>

      <fieldset>
        <legend>Tenants que va a poder facturar</legend>
        {tenants.length === 0 ? (
          <p className="nota">No hay tenants para elegir todavía.</p>
        ) : (
          <ul>
            {tenants.map((tenant) => (
              <li key={tenant.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={elegidos.includes(tenant.id)}
                    onChange={() => alternar(tenant.id)}
                  />{' '}
                  {nombreDeTenant(tenant)}
                </label>
              </li>
            ))}
          </ul>
        )}
        <p className="nota">
          Se puede dejar vacío: el sistema va a autenticarse pero no va a poder emitir para nadie
          hasta que se le autorice alguno. Emitir es lista blanca — sin autorización, 403.
        </p>
      </fieldset>

      {alta.isError ? (
        <p role="alert" className="error">
          {mensajeDeSistemas(alta.error, 'alta')}
        </p>
      ) : null}

      <button type="submit" disabled={alta.isPending}>
        {alta.isPending ? 'Dando de alta…' : 'Dar de alta'}
      </button>
      <br />
      <span className="nota">
        Al crearlo se muestra su API key una sola vez. Tenela a mano dónde guardarla.
      </span>

      {alta.data ? (
        <KeyEnClaro
          apiKey={alta.data.apiKey}
          nombre={alta.data.nombre}
          motivo="alta"
          // Cerrar el panel BORRA la key: `reset()` limpia el estado de la
          // mutación, que es el único lugar donde estuvo.
          alCerrar={() => alta.reset()}
        />
      ) : null}
    </form>
  );
}
